import { Loader2, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, authFetch } from '@/api/http-client';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROTAS } from '@/shared/lib/rotas';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

const SEGUNDOS_DE_ESPERA_PARA_REENVIAR = 30;

/**
 * Passo entre o cadastro e o dashboard: confirma que o e-mail é do dono da
 * conta antes de liberar acesso (F-registro).
 *
 * Chega só por `navigate(ROTAS.verificarEmail, { state: { email } })` —
 * digitar a URL direto sem esse `state` manda de volta pro cadastro, porque
 * não há como pedir um código novo sem saber pra qual e-mail.
 *
 * O código certo já vem com sessão: `verify-email` do plugin `emailOTP`
 * seta o cookie sozinho (`autoSignInAfterVerification` em
 * `better-auth.ts`), então depois de confirmar é só recarregar o `/me`.
 */
export function VerificarEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { recarregar } = useAuth();

  const email = (location.state as { email?: string } | null)?.email;

  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [segundosParaReenviar, setSegundosParaReenviar] = useState(SEGUNDOS_DE_ESPERA_PARA_REENVIAR);

  // `StrictMode` roda efeitos duas vezes em dev — sem a trava, o primeiro
  // acesso à tela mandaria 2 códigos (e o segundo e-mail invalidaria o
  // primeiro, confundindo quem já tinha aberto a caixa de entrada).
  const primeiroEnvioFeito = useRef(false);

  useEffect(() => {
    if (!email || primeiroEnvioFeito.current) return;
    primeiroEnvioFeito.current = true;
    void enviarCodigo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (segundosParaReenviar === 0) return;
    const id = setTimeout(() => setSegundosParaReenviar((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [segundosParaReenviar]);

  if (!email) {
    return <Navigate to={ROTAS.registro} replace />;
  }

  async function enviarCodigo(): Promise<void> {
    setReenviando(true);
    setErro(null);
    try {
      await authFetch('/email-otp/send-verification-otp', { email, type: 'email-verification' });
      setSegundosParaReenviar(SEGUNDOS_DE_ESPERA_PARA_REENVIAR);
    } catch {
      // Mesmo espírito de `EsqueciSenhaPage`: não dá pra diferenciar "e-mail
      // não existe" de "falha no envio" sem virar oráculo de contas — só
      // deixa a pessoa tentar de novo pelo botão de reenviar.
      setErro('Não foi possível enviar o código agora. Tente novamente em instantes.');
    } finally {
      setReenviando(false);
    }
  }

  async function confirmar(): Promise<void> {
    setConfirmando(true);
    setErro(null);
    try {
      await authFetch('/email-otp/verify-email', { email, otp: codigo });
      await recarregar();
      navigate(ROTAS.dashboard, { replace: true });
    } catch (e) {
      if (!(e instanceof ApiError)) {
        setErro('Não foi possível confirmar agora. Verifique a conexão e tente de novo.');
        return;
      }
      // `OTP_EXPIRED` e `TOO_MANY_ATTEMPTS`: o better-auth já apagou o código —
      // não adianta tentar de novo com o mesmo. Limpa o campo e libera o
      // "Reenviar" na hora (sem esperar o contador).
      if (e.codigo === 'OTP_EXPIRED' || e.codigo === 'TOO_MANY_ATTEMPTS') {
        setCodigo('');
        setSegundosParaReenviar(0);
      }
      setErro(
        e.codigo === 'OTP_EXPIRED'
          ? 'O código expirou. Toque em "Reenviar código" para receber um novo.'
          : e.codigo === 'TOO_MANY_ATTEMPTS'
            ? 'Muitas tentativas seguidas. Pedimos um código novo — é só tocar em "Reenviar código".'
            : e.codigo === 'INVALID_OTP'
              ? 'Código incorreto. Confira os 6 dígitos que enviamos por e-mail e tente de novo.'
              : e.message,
      );
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <AuthShell descricao="Confirme seu e-mail">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void confirmar();
        }}
        className="space-y-4"
        noValidate
      >
        <Alert className="bg-brand/10 border-brand/20">
          <MailCheck className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Enviamos um código de 6 dígitos para <strong>{email}</strong>. Ele vale por 5 minutos.
          </AlertDescription>
        </Alert>

        {erro && (
          <Alert variant="destructive" className="bg-red-100 border-red-300 text-red-800">
            <AlertDescription>{erro}</AlertDescription>
          </Alert>
        )}

        <Campo id="codigo" rotulo="Código de confirmação">
          <Input
            id="codigo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            className={`${CLASSE_CONTROLE} text-center text-2xl tracking-[0.5em] font-mono`}
            autoFocus
          />
        </Campo>

        <Button
          type="submit"
          disabled={confirmando || codigo.length !== 6}
          className="w-full bg-brand hover:bg-brand/80 text-white shadow-lg hover:shadow-xl transition-all"
        >
          {confirmando ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Confirmando...
            </>
          ) : (
            'Confirmar'
          )}
        </Button>

        <div className="text-center text-sm text-muted-foreground">
          {segundosParaReenviar > 0 ? (
            <span>Reenviar código em {segundosParaReenviar}s</span>
          ) : (
            <button
              type="button"
              onClick={() => void enviarCodigo()}
              disabled={reenviando}
              className="text-brand hover:text-brand/80 font-semibold hover:underline disabled:opacity-60"
            >
              {reenviando ? 'Reenviando...' : 'Reenviar código'}
            </button>
          )}
        </div>
      </form>
    </AuthShell>
  );
}
